/**
 * عميل لوحة المنصة.
 *
 * أبسط من عميل تطبيق المستأجرين: لا تحديد مستأجر ولا تجديد رموز. رمز
 * المنصة عمره أربع ساعات ولا يُجدَّد تلقائيًا — انتهاؤه يعيد المستخدم إلى
 * تسجيل الدخول، وهو المطلوب لحساب يرى كل الشركات.
 */

const BASE = '/api/platform';
const TOKEN_KEY = 'goyone.platform.token';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/*
 * الرمز في التخزين المحلي لا في الذاكرة وحدها.
 *
 * تطبيق المستأجرين يحفظ رمز الوصول في الذاكرة ويجدّده من كوكي، فيفقده عند
 * تحديث الصفحة ويستعيده بصمت. لوحة المنصة بلا تجديد، فالحفظ هنا هو ما
 * يمنع طلب الدخول عند كل تحديث. المقابل أن سرقة تخزين المتصفح تسرق الرمز،
 * وهو مقبول لأداة داخلية على نطاق مقيّد الوصول.
 */
export function token(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(value: string): void {
  localStorage.setItem(TOKEN_KEY, value);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const current = token();
  if (current) headers['Authorization'] = `Bearer ${current}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (res.status === 401) {
    /*
     * انتهاء الجلسة يُعالج هنا مرة واحدة.
     *
     * تركه لكل شاشة يعني أن شاشة واحدة تنسى المعالجة فتعرض للمستخدم خطأ
     * غامضًا بدل إعادته إلى الدخول.
     */
    clearToken();
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    throw new ApiError(401, 'انتهت الجلسة.');
  }

  const text = await res.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }

  if (!res.ok) {
    const message =
      (payload as { message?: string } | null)?.message ?? 'تعذّر إتمام العملية.';
    throw new ApiError(res.status, message);
  }

  return payload as T;
}

/** يبني سلسلة استعلام، متجاهلًا القيم الفارغة. */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}
